import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { APIRequestStatus } from 'common-ts/dist/models/APIRequestStatus';
import { RootState } from '../store';
import { Address, AddressListState } from 'common-ts/dist/models/Address';
import { terbebasClient } from '../terbebasApi';
import { normalizeIndonesianPhone } from 'common-ts/dist/models/Phone';

// Define the initial state using that type
const initialState: AddressListState = {
  addressDraft: {},
  addressList: [],
  status: APIRequestStatus.Idle,
  error: null,
}

// terbebas Address row -> legacy Address shape the UI renders.
function mapToLegacyAddress(a: any): Address {
  return {
    creationDateTime: a.createdAt,
    addressName: a.label || a.recipientName,
    address: a.addressDetail,
    kodePos: Number(a.postalCode) || undefined,
    kotaKecamatanText: [a.city, a.district].filter(Boolean).join(', '),
    keterangan: '',
    phoneNumber: a.phone,
    provinsi: a.province,
    kabupaten: a.city,
    kecamatan: a.district,
    // Minimal KiriminAja destination object for courier rate quotes.
    destinationKiriminAjaObject: a.kiriminAjaDistrictId
      ? { kecamatan_id: a.kiriminAjaDistrictId, kecamatan_name: a.district, kabupaten_name: a.city, provinsi_name: a.province }
      : undefined,
  };
}

// legacy draft -> terbebas address input.
function mapDraftToTerbebas(draft: Address, fallbackName: string) {
  return {
    label: draft.addressName ?? '',
    recipientName: draft.addressName || fallbackName || 'Penerima',
    // Canonical 628… form (the backend validates and stores it as such).
    phone: normalizeIndonesianPhone(draft.phoneNumber) || (draft.phoneNumber ?? ''),
    province: draft.provinsi ?? '',
    city: draft.kabupaten ?? draft.kotaKecamatanText ?? '',
    district: draft.kecamatan ?? '',
    postalCode: draft.kodePos !== undefined ? String(draft.kodePos) : '',
    addressDetail: [draft.address, draft.keterangan].filter(Boolean).join(' — '),
    kiriminAjaDistrictId: draft.destinationKiriminAjaObject?.kecamatan_id ?? undefined,
  };
}

export const fetchAddresses = createAsyncThunk('addressList/fetchAddresses',
  async (payload, thunkAPI) => {
    const rootState = (thunkAPI.getState() as RootState);
    const idToken = rootState.account.idToken;
    if (!idToken) {
      return { addresses: [] };
    }

    const response = await terbebasClient.get('/storefront/auth/me', {
      headers: { 'Authorization': 'Bearer ' + idToken },
    });
    // The profile's phone rides along so AccountRedux can hydrate the
    // account phone (login responses never carried it).
    return { addresses: response.data.addresses ?? [], phone: response.data.phone ?? null };
  })

export const postAddress = createAsyncThunk(
  'addressList/postAddress',
  async (payload: string | undefined, thunkAPI) => {
    const rootState = (thunkAPI.getState() as RootState);
    const addressDraft = rootState.addressList.addressDraft;
    const idToken = payload ? payload : rootState.account.idToken;
    if (!idToken) {
      return null;
    }

    const response = await terbebasClient.post(
      '/storefront/addresses',
      mapDraftToTerbebas(addressDraft, rootState.account.name),
      { headers: { 'Authorization': 'Bearer ' + idToken } }
    );

    thunkAPI.dispatch(fetchAddresses());

    return response.data
  });

export const slice = createSlice({
  name: 'addressList',
  initialState,
  reducers: {
    resetAddressDraft: (state) => {
      state.addressDraft = {};
    },

    resetAddressList: (state) => {
      state.addressList = [];
      state.status = APIRequestStatus.Idle;
    },

    inputAddressForm: (state, action: PayloadAction<Address>) => {
      Object.assign(state.addressDraft, action.payload);
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchAddresses.pending, (state, action) => {
        state.status = APIRequestStatus.RequestInProgress;
      })
      .addCase(fetchAddresses.fulfilled, (state, action) => {
        state.addressList = (action.payload.addresses ?? []).map(mapToLegacyAddress);
        state.status = APIRequestStatus.Success;
      })
      .addCase(fetchAddresses.rejected, (state, action) => {
        state.status = APIRequestStatus.Failure;
        console.log("fetch is failure");
      })
      .addCase(postAddress.pending, (state, action) => {
      })
      .addCase(postAddress.fulfilled, (state, action) => {
      })
      .addCase(postAddress.rejected, (state, action) => {
      })
  }
})

// Action creators are generated for each case reducer function
export const { resetAddressDraft, resetAddressList, inputAddressForm } = slice.actions

export default slice.reducer
